import { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { setOktaAuth } from './api/client';
import { LoadingScreen } from './components/LoadingScreen';
import { AccessDeniedScreen } from './components/screens/AccessDeniedScreen';
import { PackageNotFoundScreen } from './components/screens/PackageNotFoundScreen';
import { ErrorScreen } from './components/screens/ErrorScreen';
import { ReportScreen } from './components/screens/ReportScreen';
import { PreQCReportScreen } from './components/screens/PreQCReportScreen';
import { AnalysisInProgressScreen } from './components/screens/AnalysisInProgressScreen';
import { usePDFExport } from './hooks/usePDFExport';
import { useReportManagement } from './hooks/useReportManagement';
import { usePackageSummary } from './hooks/usePackageSummary';
// Timecode conversion removed - now handled by backend
import type {
  ScreenMode,
  ViewMode,
  Issue,
} from './types/reportApp';


interface AppProps {
  initialScreen?: ScreenMode;
  initialReportId?: string;
  showApproveButton?: boolean;
  onApproveReport?: (params: { reportId: string; issues: Issue[] }) => void;
  isApprovingReport?: boolean;
  readOnly?: boolean;
  onBack?: () => void;
}

function App({
  initialScreen = 'report',
  initialReportId,
  showApproveButton = false,
  onApproveReport,
  isApprovingReport = false,
  readOnly = false,
  onBack,
}: AppProps = {}) {
  const oktaAuthContext = useOktaAuth();
  const oktaAuth = oktaAuthContext?.oktaAuth;

  // Pass OktaAuth instance to API client so it can access tokens
  useEffect(() => {
    if (oktaAuth) {
      console.log('🔧 Setting oktaAuth instance in API client');
      setOktaAuth(oktaAuth);
    }
  }, [oktaAuth]);

  // UI state
  const [screen] = useState<ScreenMode>(initialScreen);
  const [viewMode, setViewMode] = useState<ViewMode>('issue');
  const [showAddModal, setShowAddModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [uploadProgress] = useState(0);
  const [uploadStatus] = useState('Preparing...');

  // Toast notification
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), 3000);
  };

  // Report management hook
  const {
    packageName,
    issues,
    editingIssue,
    setEditingIssue,
    reportId,
    isLoadingReport,
    accessDenied,
    packageNotFound,
    hasError,
    reportStatus,
    notes,
    handleApprove,
    handleDismiss,
    handleMoveToPending,
    handleUpdateIssue,
    handleAddIssue,
    handleNotesUpdate,
    pendingIssues,
    approvedIssues,
  } = useReportManagement({
    reportId: initialReportId,
    screen,
    onToast: showToast,
  });

  // Package summary hook
  const { summary: packageSummary } = usePackageSummary(reportId);

  // PDF Export hook
  const { exportPDF } = usePDFExport();

  const handleDownloadPDF = async () => {
    await exportPDF(
      {
        reportId,
        packageName,
        issues,
        reportStatus,
        packageSummary,
        notes,
      },
      () => showToast('PDF downloaded!')
    );
  };

  // Render appropriate screen
  if (screen === 'loading') {
    return <LoadingScreen uploadProgress={uploadProgress} uploadStatus={uploadStatus} />;
  }

  // Access denied
  if (accessDenied) {
    return <AccessDeniedScreen onBack={onBack} />;
  }

  // Package not found (404)
  if (packageNotFound) {
    return <PackageNotFoundScreen onBack={onBack} />;
  }

  // Generic error (all other errors)
  if (hasError) {
    return <ErrorScreen onBack={onBack} />;
  }

  // Pre-QC reports get a simplified view
  if (reportStatus === 'preqc') {
    return <PreQCReportScreen
      packageName={packageName}
      reportId={reportId}
      issues={issues}
      isLoadingReport={isLoadingReport}
      reportStatus={reportStatus}
      onHandleDownloadPDF={handleDownloadPDF}
      onBack={onBack}
    />;
  }

  // Analysis in progress - show waiting screen
  if (reportStatus === 'inprogress') {
    return <AnalysisInProgressScreen onBack={onBack} />;
  }

  // Full QC reports get the full interactive view
  return <ReportScreen
    packageName={packageName}
    reportId={reportId}
    issues={issues}
    pendingIssues={pendingIssues}
    approvedIssues={approvedIssues}
    isLoadingReport={isLoadingReport}
    viewMode={viewMode}
    editingIssue={editingIssue}
    showAddModal={showAddModal}
    toastMessage={toastMessage}
    reportStatus={reportStatus}
    showApproveButton={showApproveButton}
    isApprovingReport={isApprovingReport}
    readOnly={readOnly}
    notes={notes}
    onViewModeChange={setViewMode}
    onSetEditingIssue={setEditingIssue}
    onSetShowAddModal={setShowAddModal}
    onHandleUpdateIssue={handleUpdateIssue}
    onHandleApprove={handleApprove}
    onHandleMoveToPending={handleMoveToPending}
    onHandleDismiss={handleDismiss}
    onHandleAddIssue={handleAddIssue}
    onHandleDownloadPDF={handleDownloadPDF}
    onHandleNotesUpdate={handleNotesUpdate}
    onHandleApproveReport={() => {
      console.log('Approve button clicked, reportId:', reportId, 'issues:', issues);
      onApproveReport?.({ reportId, issues });
    }}
    onBack={onBack}
  />;
}

export default App;
